import { fork, put, select } from 'redux-saga/effects';

import { selectTyped, takeEvery } from '@pressreader/appstore';
import { ArticleFields } from '@pressreader/content-types';
import { FeedCursorType } from '@pressreader/feed-types';
import { error } from '@pressreader/logger';
import { navigateToAction, selectNavConfig } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';
import { closeContextMenu, openContextMenu, takeCloseContextMenuRequest } from '@pressreader/popups-legacy';
import { Align, IContextMenuPosition } from '@pressreader/popups-types';
import { isArticleNavConfig } from '@pressreader/views-navigation';

import { selectIsContextMenuEnabled } from '../../global.selectors';
import { ArticleContextMenuContext, ArticleContextMenuName, ArticleTranslateContextMenuContext } from '../../types';
import {
    articleMenuClosed,
    articleMenuOpened,
    editArticleAction,
    navigateToArticleAction,
    openArticleMenuAction,
    openTranslationMenuAction,
    translateArticleAction,
} from '../actions';
import { loadArticleById, loadArticleDataForContextMenu } from '../saga.utils';

function* openArticleMenu({ payload }: ReturnType<typeof openArticleMenuAction>) {
    const isContextMenuEnabled: ReturnType<typeof selectIsContextMenuEnabled> = yield select(selectIsContextMenuEnabled);
    if (!isContextMenuEnabled) {
        return;
    }

    const position: IContextMenuPosition = {
        center: payload.center,
        align: {
            y: Align.Center,
            x: Align.Center,
        },
    };

    const articleId = payload.articleId;

    const article = yield* loadArticleById(articleId, {
        lang: payload.language,
        requiredFields: ArticleFields.Issue | ArticleFields.Publication,
        force: false,
    });

    if (!article) {
        error(`article.core.saga.openArticleMenu. Article not found by id: ${articleId}`);
        return;
    }

    yield fork(loadArticleDataForContextMenu, article);

    const primaryArticleId: string | undefined = payload.primaryArticleId || article.primaryArticleId;

    const ctxMenuId = yield* openContextMenu<ArticleContextMenuContext>({
        name: ArticleContextMenuName.ArticleContextMenu,
        context: {
            articleId,
            primaryArticleId,
            allowEditing: !payload.proxied, // allow editing only from article view
            issueViewNavigation: payload.issueViewNavigation,
            textViewContextKey: payload.textViewContextKey,
            pageViewContextKey: payload.pageViewContextKey,
            cursor: payload.cursor,
            language: payload.language,
        },
        position,
    });

    yield put(articleMenuOpened({ articleId }));

    const { payload: closeMenuPayload } = yield* takeCloseContextMenuRequest(ctxMenuId);

    yield* closeContextMenu(ctxMenuId, { back: closeMenuPayload.back });
    yield put(articleMenuClosed({ articleId }));
}

function* editArticle({ payload }: ReturnType<typeof editArticleAction>) {
    yield put(navigateToAction({ viewName: ViewName.Post, params: { id: payload.articleId } }));
}

function* openTranslationMenu({ payload }: ReturnType<typeof openTranslationMenuAction>) {
    const position: IContextMenuPosition = payload.center
        ? {
              center: payload.center,
              align: {
                  y: Align.Center,
                  x: Align.Center,
              },
          }
        : { center: { x: 0, y: 0 }, align: { y: Align.ScreenCenter, x: Align.ScreenCenter } };

    const { articleId, cursor, language } = payload;
    const ctxMenuId = yield* openContextMenu<ArticleTranslateContextMenuContext>({
        name: ArticleContextMenuName.ArticleTranslationContextMenu,
        context: { articleId, cursor, language: language ?? undefined },
        position,
        parentMenuId: payload.parentMenuId,
    });

    const {
        payload: { back },
    } = yield* takeCloseContextMenuRequest(ctxMenuId);

    yield* closeContextMenu(ctxMenuId, { back });
}

export function* translateArticle({ payload }: ReturnType<typeof translateArticleAction>) {
    const { articleId, languageCode } = payload;
    let { cursor } = payload;

    const currentNavConfig = yield* selectTyped(selectNavConfig);

    const replace = isArticleNavConfig(currentNavConfig) && articleId === currentNavConfig.articleId;

    // A pick belongs to the article, not to the feed: the cursor passes through untouched and only `languageCode`
    // carries the pick. A cursor pointing at another article belongs to no navigation from here.
    if (cursor?.cursorType === FeedCursorType.Article && cursor.articleId !== payload.articleId) {
        cursor = null;
    }

    yield put(navigateToArticleAction({ cursor, articleId, languageCode, replace }));
}

export function* saga() {
    yield takeEvery(openArticleMenuAction, openArticleMenu);
    yield takeEvery(editArticleAction, editArticle);
    yield takeEvery(openTranslationMenuAction, openTranslationMenu);
    yield takeEvery(translateArticleAction, translateArticle);
}
